
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Clock, Monitor, Globe, Calendar } from "lucide-react";
import { RrwebSession } from '@/hooks/useRrwebSessions';
import { SimpleResponsiveRrwebPlayer } from '@/components/playback/SimpleResponsiveRrwebPlayer';

interface SessionPreviewProps {
  session: RrwebSession | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect?: (session: RrwebSession) => void;
}

const SessionPreview = ({ session, open, onOpenChange, onSelect }: SessionPreviewProps) => {
  if (!session) return null;

  const formatDuration = (ms?: number) => {
    if (!ms) return 'Unknown duration';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString([], { 
      weekday: 'long',
      year: 'numeric',
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-editor-medium border-editor-light text-white max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {session.name || `Recording Session`}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Preview and details for this recording session
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Player Area - Fixed container with proper aspect ratio */}
          <div className="w-full">
            {session.events_storage_path ? (
              <SimpleResponsiveRrwebPlayer
                sessionEventsStoragePath={session.events_storage_path}
                autoPlay={false}
                className="w-full aspect-video"
              />
            ) : (
              <div className="aspect-video bg-editor-dark rounded-lg flex items-center justify-center">
                <p className="text-gray-400">No events path available for this session.</p>
              </div>
            )}
          </div>

          {/* Session Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-center text-sm">
                <Clock className="h-4 w-4 mr-2 text-editor-accent" />
                <span className="text-gray-300">Duration:</span>
                <span className="ml-2">{formatDuration(session.duration_ms)}</span>
              </div>

              {session.viewport_width && session.viewport_height && (
                <div className="flex items-center text-sm">
                  <Monitor className="h-4 w-4 mr-2 text-editor-accent" />
                  <span className="text-gray-300">Resolution:</span>
                  <span className="ml-2">{session.viewport_width} × {session.viewport_height}</span>
                </div>
              )}

              <div className="flex items-center text-sm">
                <Calendar className="h-4 w-4 mr-2 text-editor-accent" />
                <span className="text-gray-300">Recorded:</span>
                <span className="ml-2">{formatDate(session.created_at)}</span>
              </div>
            </div>

            <div className="space-y-3">
              {session.initial_url && (
                <div className="flex items-start text-sm">
                  <Globe className="h-4 w-4 mr-2 text-editor-accent mt-0.5" />
                  <div>
                    <span className="text-gray-300 block">Initial URL:</span>
                    <span className="text-xs text-blue-400 break-all">{session.initial_url}</span>
                  </div>
                </div>
              )}

              {session.generated_script && (
                <div className="text-sm">
                  <span className="text-gray-300 block mb-1">AI Generated Script:</span>
                  <p className="text-xs text-gray-400 bg-editor-dark p-2 rounded max-h-20 overflow-y-auto">
                    {session.generated_script}
                  </p>
                </div>
              )}
            </div>
          </div>

          {session.description && (
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-2">Description</h4>
              <p className="text-sm text-gray-400 bg-editor-dark p-3 rounded">
                {session.description}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-editor-light">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-editor-light hover:bg-editor-light"
            >
              Close
            </Button>
            {onSelect && (
              <Button
                onClick={() => {
                  onSelect(session);
                  onOpenChange(false);
                }}
                className="flex-1 bg-editor-accent hover:bg-editor-highlight"
              >
                Select Session
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SessionPreview;
